/**
 * UI.ts — Heads-Up Display and minimap.
 *
 * Reads game state each frame and updates DOM HUD elements
 * (speed, altitude, throttle, fuel, gear, score, mission text).
 * Also draws a canvas-based minimap showing terrain, airstrips,
 * storm clouds, fuel pickups, and the player position.
 */
import * as THREE from 'three';
import { loadMobileSettings, isTouchDevice } from './MobileControls';
export class UI {
    constructor(game) {
        /* Flash message state */
        this.msgTimer = 0;
        this.game = game;
        this.elAlt = document.getElementById('hud-alt');
        this.elSpeed = document.getElementById('hud-speed');
        this.elThrottle = document.getElementById('hud-throttle');
        this.elScore = document.getElementById('hud-score');
        this.elFuelBar = document.getElementById('fuel-bar');
        this.elGear = document.getElementById('hud-gear');
        this.elMission = document.getElementById('hud-mission');
        this.elMessage = document.getElementById('hud-message');
        this.elNavCompass = document.getElementById('nav-compass');
        this.elNavArrow = document.getElementById('nav-arrow');
        this.elNavDistance = document.getElementById('nav-distance');
        this.elThrottleBar = document.getElementById('throttle-bar');
        this.miniCanvas = document.getElementById('minimap');
        this.miniCtx = this.miniCanvas.getContext('2d');
        /* Move minimap to opposite side of D-pad on mobile */
        if (isTouchDevice()) {
            const settings = loadMobileSettings();
            // D-pad on left → minimap on right, and vice versa
            if (settings.dpadSide === 'left') {
                this.miniCanvas.classList.add('minimap-right');
            }
            else {
                this.miniCanvas.classList.remove('minimap-right');
            }
        }
    }
    show() {
        document.getElementById('hud').classList.remove('hidden');
        this.miniCanvas.classList.remove('hidden');
    }
    hide() {
        document.getElementById('hud').classList.add('hidden');
        this.miniCanvas.classList.add('hidden');
    }
    flashMessage(msg) {
        this.elMessage.textContent = msg;
        this.elMessage.style.opacity = '1';
        this.msgTimer = 2.5;
    }
    update(dt) {
        const p = this.game.player;
        /* Numeric displays */
        const terrainH = this.game.world.getTerrainHeight(p.mesh.position.x, p.mesh.position.z);
        const alt = Math.max(0, p.mesh.position.y - terrainH);
        this.elAlt.textContent = Math.round(alt).toString();
        this.elSpeed.textContent = Math.round(p.speed * 1.944).toString(); // m/s → knots approx
        const throttlePct = Math.round(p.throttle * 100);
        this.elThrottle.textContent = throttlePct.toString();
        this.elThrottleBar.style.height = `${throttlePct}%`;
        this.elScore.textContent = this.game.score.toString();
        this.elGear.textContent = p.gearDown ? 'DOWN' : 'UP';
        this.elGear.style.color = p.gearDown ? '#56ffa4' : '#ff6655';
        this.elFuelBar.style.width = `${this.game.fuel}%`;
        /* Mission text */
        this.elMission.textContent = this.game.missions.missionText;
        /* Flash message fade-out */
        if (this.msgTimer > 0) {
            this.msgTimer -= dt;
            if (this.msgTimer <= 0) {
                this.elMessage.style.opacity = '0';
            }
        }
        /* Minimap (redraw every frame) */
        this.drawMinimap();
        /* Mission navigation arrow */
        this.updateNavigationArrow();
    }
    updateNavigationArrow() {
        const target = this.game.missions.navigationTarget;
        if (!target) {
            this.elNavCompass.style.opacity = '0.45';
            this.elNavDistance.textContent = '-- m';
            this.elNavArrow.style.transform = 'rotate(0rad)';
            return;
        }
        this.elNavCompass.style.opacity = '1';
        const playerPos = this.game.player.mesh.position;
        const toTarget = target.clone().sub(playerPos);
        const planarToTarget = new THREE.Vector3(toTarget.x, 0, toTarget.z);
        const distance = planarToTarget.length();
        this.elNavDistance.textContent = `${Math.round(distance)} m`;
        if (distance < 0.001) {
            this.elNavArrow.style.transform = 'rotate(0rad)';
            return;
        }
        planarToTarget.normalize();
        const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.game.player.mesh.quaternion);
        const planarForward = new THREE.Vector3(forward.x, 0, forward.z).normalize();
        // Use atan2(x, -z) so that facing -Z (forward) = 0 and east (+X) = +π/2
        const playerYaw = Math.atan2(planarForward.x, -planarForward.z);
        const targetYaw = Math.atan2(planarToTarget.x, -planarToTarget.z);
        let delta = targetYaw - playerYaw;
        while (delta > Math.PI)
            delta -= Math.PI * 2;
        while (delta < -Math.PI)
            delta += Math.PI * 2;
        this.elNavArrow.style.transform = `rotate(${delta}rad)`;
    }
    /* ===== MINIMAP ===== */
    drawMinimap() {
        const ctx = this.miniCtx;
        const w = this.miniCanvas.width;
        const h = this.miniCanvas.height;
        const cx = w / 2;
        const cy = h / 2;
        const scale = w / 1200; // world units per pixel
        ctx.clearRect(0, 0, w, h);
        // Clip to circle
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, cx - 1, 0, Math.PI * 2);
        ctx.clip();
        // Background
        ctx.fillStyle = 'rgba(10,30,60,0.9)';
        ctx.fillRect(0, 0, w, h);
        const px = this.game.player.mesh.position.x;
        const pz = this.game.player.mesh.position.z;
        // Compute player heading from velocity for map rotation
        const vx = this.game.player.velocity.x;
        const vz = this.game.player.velocity.z;
        const heading = Math.atan2(vx, -vz);
        /* Rotate the WORLD layer so the player's forward direction sits at the
           top of the minimap. The player arrow stays fixed pointing straight up. */
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(-heading);
        ctx.translate(-cx, -cy);
        const toScreen = (wx, wz) => {
            return [cx + (wx - px) * scale, cy + (wz - pz) * scale];
        };
        /* Airstrips */
        ctx.fillStyle = '#aaa';
        for (const strip of this.game.world.airstrips) {
            const [sx, sy] = toScreen(strip.position.x, strip.position.z);
            ctx.fillRect(sx - 3, sy - 1, 6, 2);
        }
        /* Storm clouds */
        ctx.fillStyle = 'rgba(100,100,180,0.4)';
        for (const sc of this.game.world.stormClouds) {
            const [sx, sy] = toScreen(sc.center.x, sc.center.z);
            const r = sc.radius * scale;
            ctx.beginPath();
            ctx.arc(sx, sy, r, 0, Math.PI * 2);
            ctx.fill();
        }
        /* Fuel pickups */
        ctx.fillStyle = '#ffcc00';
        for (const fp of this.game.world.fuelPickups) {
            if (fp.collected)
                continue;
            const [sx, sy] = toScreen(fp.mesh.position.x, fp.mesh.position.z);
            ctx.beginPath();
            ctx.arc(sx, sy, 2, 0, Math.PI * 2);
            ctx.fill();
        }
        /* Landmarks */
        ctx.fillStyle = '#ff4444';
        for (const lm of this.game.world.landmarks) {
            const [sx, sy] = toScreen(lm.x, lm.z);
            ctx.beginPath();
            ctx.arc(sx, sy, 3, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.restore(); // undo map rotation
        /* Player arrow: always fixed pointing up at the centre */
        ctx.fillStyle = '#56ffa4';
        ctx.save();
        ctx.translate(cx, cy);
        ctx.beginPath();
        ctx.moveTo(0, -7);
        ctx.lineTo(-4, 5);
        ctx.lineTo(0, 2);
        ctx.lineTo(4, 5);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        ctx.restore(); // un-clip
    }
}
